import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  Eye, Share2, Store, Tag, ExternalLink, Copy, Check, Lock, TrendingUp,
  QrCode, UserPlus, Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CHANNEL_LABELS = {
  facebook: "Facebook",
  whatsapp: "WhatsApp",
  x: "X",
  linkedin: "LinkedIn",
  copy: "Lien copié",
  native: "Partage natif",
  other: "Autre",
};
const CHANNEL_COLORS = {
  facebook: "#1877F2",
  whatsapp: "#25D366",
  x: "#111827",
  linkedin: "#0A66C2",
  copy: "#64748b",
  native: "#E05638",
  other: "#94a3b8",
};

function StatCard({ icon: Icon, label, value, hint, color = "text-[#E05638]", tid }) {
  const v = Number(value || 0);
  const empty = v <= 0;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4 md:p-5" data-testid={tid}>
      <Icon size={18} className={color} />
      <div className={`mt-2 font-heading text-2xl md:text-3xl font-extrabold ${empty ? "text-slate-300" : "text-slate-900"}`}>
        {empty ? "—" : v.toLocaleString("fr-FR")}
      </div>
      <div className="text-[10px] md:text-xs uppercase tracking-widest text-slate-500 font-bold mt-1">{label}</div>
      {empty ? (
        <div className="mt-1 text-[11px] text-slate-400">Dès les premières interactions</div>
      ) : (
        hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>
      )}
    </div>
  );
}

export default function ProStats() {
  const [data, setData] = useState(null);
  const [quota, setQuota] = useState(null);
  const [copied, setCopied] = useState(false);
  const [report, setReport] = useState(null);
  const [month, setMonth] = useState("");

  useEffect(() => {
    api.get("/pro/vitrine-stats").then((r) => setData(r.data)).catch(() => setData({ has_business: false }));
    api.get("/pro/offers/quota").then((r) => setQuota(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/pro/monthly-report", { params: month ? { month } : {} })
      .then((r) => { setReport(r.data); if (!month && r.data?.month) setMonth(r.data.month); })
      .catch(() => {});
  }, [month]);

  if (!data) return <div className="text-slate-500">Chargement…</div>;

  if (!data.has_business) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="pro-stats-no-business">
        <Store size={36} className="mx-auto text-slate-300" />
        <h1 className="mt-3 font-heading text-xl font-extrabold text-slate-900">Complétez votre mini-site</h1>
        <p className="mt-1 text-sm text-slate-500">Créez votre mini-site local pour suivre ses vues et partages.</p>
        <Link to="/pro/entreprise" className="inline-block mt-4">
          <Button className="btn-shine text-white" data-testid="pro-stats-create-business">Créer mon mini-site</Button>
        </Link>
      </div>
    );
  }

  const isFree = quota && quota.limit === 0;
  const totals = data.channel_totals || {};
  const maxChan = Math.max(1, ...Object.values(totals).map((n) => Number(n || 0)));
  const activeChannels = (data.channels || []).filter((c) => Number(totals[c] || 0) > 0);

  const vitrineUrl = data.vitrine_url || "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(vitrineUrl);
      setCopied(true);
      toast.success("Lien de votre mini-site copié !");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier le lien");
    }
  };

  return (
    <div className="space-y-8" data-testid="pro-stats">
      <header>
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Statistiques de visibilité</h1>
        <p className="text-sm text-slate-500 mt-1">
          Vues et partages de votre mini-site local (vitrine) et de vos offres.
        </p>
      </header>

      {/* Bilan du mois — ROI */}
      {report?.has_business && (
        <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-orange-50 p-5 md:p-6" data-testid="pro-monthly-report">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900 text-lg">
              <Calendar size={18} className="text-emerald-600" /> Votre bilan du mois
            </div>
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-44 h-9 bg-white" data-testid="pro-report-month"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(report.months || []).map((m) => (<SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="report-views">
              <Eye size={18} className="text-blue-600" />
              <div className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{(report.views || 0).toLocaleString("fr-FR")}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-1">Vues de vos offres</div>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="report-scans">
              <QrCode size={18} className="text-[#E05638]" />
              <div className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{(report.scans || 0).toLocaleString("fr-FR")}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-1">Visites confirmées (scans)</div>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 p-4" data-testid="report-new-clients">
              <UserPlus size={18} className="text-emerald-600" />
              <div className="mt-2 font-heading text-3xl font-extrabold text-slate-900">{(report.new_clients || 0).toLocaleString("fr-FR")}</div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-bold mt-1">Nouveaux clients</div>
            </div>
          </div>

          <p className="mt-4 text-sm text-slate-600" data-testid="report-sentence">
            En <b>{report.month_label}</b>, votre présence sur PROXIreduc-28 a généré
            {" "}<b className="text-slate-900">{report.views || 0} vues</b>,
            {" "}<b className="text-slate-900">{report.scans || 0} visites confirmées</b> en boutique et
            {" "}<b className="text-emerald-700">{report.new_clients || 0} nouveaux clients</b>.
            {(report.requests > report.scans) && (
              <> {report.requests} offres ont été demandées : pensez à valider le QR code de chaque client pour suivre vos visites.</>
            )}
          </p>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <StatCard tid="pro-stats-vitrine-views" icon={Eye} label="Vues de votre mini-site" value={data.vitrine_views_total}
                  color="text-blue-600" hint={data.vitrine_views_30d > 0 ? `${data.vitrine_views_30d} sur 30 j` : null} />
        <StatCard tid="pro-stats-vitrine-shares" icon={Share2} label="Partages du mini-site" value={data.vitrine_shares} color="text-[#E05638]" />
        <StatCard tid="pro-stats-offers-shares" icon={Tag} label="Partages de vos offres" value={data.offers_shares} color="text-emerald-600" />
        <StatCard tid="pro-stats-total-shares" icon={TrendingUp} label="Total des partages" value={data.total_shares} color="text-indigo-600" />
      </div>

      {/* Courbe d'évolution des vues de la fiche */}
      {(() => {
        const serie = (data.vitrine_series || []).map((d) => ({
          views: d.views,
          label: `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`,
        }));
        const hasData = serie.some((d) => d.views > 0);
        return (
          <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-stats-vitrine-chart">
            <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
              <TrendingUp size={18} className="text-blue-600" /> Évolution des vues de votre mini-site
              <span className="text-xs font-normal text-slate-400">· 30 derniers jours</span>
            </div>
            {!hasData ? (
              <div className="mt-4 h-56 grid place-items-center text-center" data-testid="pro-stats-vitrine-chart-empty">
                <div>
                  <Eye size={34} className="mx-auto text-slate-300" />
                  <div className="mt-2 text-sm text-slate-600 font-semibold">Pas encore de vues mesurées</div>
                  <div className="mt-1 text-xs text-slate-400 max-w-xs mx-auto">
                    La courbe des visites de votre mini-site apparaîtra ici dès que des visiteurs le consulteront. Partagez votre lien pour accélérer !
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 h-56">
                <ResponsiveContainer>
                  <AreaChart data={serie} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                    <defs>
                      <linearGradient id="vitrineViews" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                    <XAxis dataKey="label" fontSize={10} tickLine={false} axisLine={{ stroke: "#eee" }}
                           interval="preserveStartEnd" minTickGap={24} />
                    <YAxis fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 12, border: "1px solid #eee" }} />
                    <Area type="monotone" dataKey="views" name="Vues" stroke="#2563eb" strokeWidth={2} fill="url(#vitrineViews)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        );
      })()}

      {/* Partager ma fiche */}
      <div className="bg-gradient-to-br from-[#E05638]/5 to-orange-50 border border-orange-200 rounded-2xl p-5" data-testid="pro-stats-share-card">
        <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
          <Share2 size={18} className="text-[#E05638]" /> Faites connaître votre mini-site
        </div>
        <p className="mt-1 text-sm text-slate-600">
          Plus votre mini-site est partagé, plus il attire de clients et remonte dans Google. Partagez ce lien :
        </p>
        <div className="mt-3 flex flex-col sm:flex-row gap-2">
          <input readOnly value={vitrineUrl} data-testid="pro-stats-vitrine-url"
                 className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600 truncate" />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="pro-stats-copy-link">
              {copied ? <Check size={15} className="mr-1.5" /> : <Copy size={15} className="mr-1.5" />}
              {copied ? "Copié" : "Copier"}
            </Button>
            <a href={vitrineUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm" data-testid="pro-stats-open-vitrine">
                <ExternalLink size={15} className="mr-1.5" /> Voir
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Répartition des partages par canal */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-stats-channels">
        <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
          <Share2 size={18} className="text-[#E05638]" /> Partages par réseau
        </div>
        {activeChannels.length === 0 ? (
          <div className="mt-4 text-sm text-slate-400" data-testid="pro-stats-channels-empty">
            Aucun partage mesuré pour l'instant. Partagez votre mini-site et vos offres pour voir la répartition ici.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {activeChannels.map((c) => {
              const n = Number(totals[c] || 0);
              return (
                <div key={c} className="flex items-center gap-3" data-testid={`pro-stats-channel-${c}`}>
                  <div className="w-24 text-xs font-semibold text-slate-600 shrink-0">{CHANNEL_LABELS[c] || c}</div>
                  <div className="flex-1 h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full rounded-full transition-all"
                         style={{ width: `${Math.round((n / maxChan) * 100)}%`, backgroundColor: CHANNEL_COLORS[c] || "#94a3b8" }} />
                  </div>
                  <div className="w-10 text-right text-xs font-bold text-slate-800">{n}</div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Top offres partagées */}
      {(data.top_offers || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pro-stats-top-offers">
          <div className="flex items-center gap-2 font-heading font-extrabold text-slate-900">
            <Tag size={18} className="text-emerald-600" /> Vos offres les plus partagées
          </div>
          <div className="mt-4 divide-y divide-slate-100">
            {data.top_offers.map((o, i) => (
              <div key={o.id} className="flex items-center gap-3 py-2.5" data-testid={`pro-stats-top-offer-${i}`}>
                <div className="w-6 h-6 grid place-items-center rounded-full bg-slate-100 text-xs font-bold text-slate-600 shrink-0">{i + 1}</div>
                <Link to={`/pro/offres/${o.id}/editer`} className="flex-1 text-sm font-semibold text-slate-800 truncate hover:text-[#E05638]">{o.title}</Link>
                <div className="text-xs text-slate-400 shrink-0">{o.views} vues</div>
                <div className="flex items-center gap-1 text-xs font-bold text-emerald-700 shrink-0">
                  <Share2 size={13} /> {o.shares}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upsell plan gratuit */}
      {isFree && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-5 flex flex-col md:flex-row items-start md:items-center gap-3" data-testid="pro-stats-upsell">
          <Lock size={20} className="text-amber-600 shrink-0" />
          <div className="flex-1">
            <div className="font-heading font-extrabold text-slate-900">Multipliez votre visibilité</div>
            <p className="text-sm text-slate-600 mt-0.5">
              Avec une formule payante, publiez des offres partageables qui attirent plus de vues sur votre mini-site et de nouveaux clients.
            </p>
          </div>
          <Link to="/pro/abonnement">
            <Button className="btn-shine text-white" data-testid="pro-stats-upsell-btn">Voir les formules</Button>
          </Link>
        </div>
      )}
    </div>
  );
}
